import type { LiquidityCategory, LiquidityPeriod, LiquidityPosition, LiquiditySeries } from "@kundeninfo/api-client";
import { hausregelTooltip } from "@kundeninfo/ui/diagramm";
import type { DiagrammThema } from "@kundeninfo/ui/diagramm";
import { addDays, dayMs, isoWeek, weekday, windowFromMs } from "../tickets/timelineModel.js";
import { heuteBeschriftung, monatBeschriftung, tageZwischen } from "../tickets/ticketFormat.js";
import { shortDay } from "../tickets/timelineTexts.js";
import { LIQUIDITY_TEXT_WEB as T } from "./liquidityTexts.js";
import type { DraftCounts } from "./liquidityTexts.js";

/**
 * MODELL DER LIQUIDITÄT (Spec „Oberfläche“, Plan P8–P10, P12) — reine Funktionen, kein React. Abschnitte, Diagrammoption,
 * Tabelle, Positionsgruppen, Entwurf und Hinweise werden hier gerechnet, die Komponenten zeichnen nur. Auf das DOM greift allein
 * der Formatter des Popups zu, erst beim Zeigen (`popupNode`).
 * Alle Zeitwerte sind Kalendertage (`2026-10-31`), auf der Achse UTC-Mitternacht (`dayMs`); ein Tag reicht von `dayMs(tag)`
 * bis `dayMs(tag) + 1 Tag`, keine lokale Zeitzone.
 */

type Zone = LiquiditySeries["zones"][number]["kind"];
type Granularity = LiquiditySeries["granularity"];
type Span = { from: string; to: string };

export const ZONE_GAP_PX = 12, QUARTER_SHARE = 0.045, YEAR_SHARE = 0.06;
/** Die Verdichtungen in der Folge des Umschalters. */
export const GRANULARITIES = ["DAY", "WEEK", "MONTH", "MONTH_END", "FIVE_YEARS"] as const satisfies readonly Granularity[];

const DAY_MS = 86_400_000;
const GRID_TOP_PX = 18, GRID_BOTTOM_PX = 24, MARK_PX = 4, HIT_PX = 4, AXIS_FONT_PX = 11, AXIS_LABEL_PX = 56, BELOW_OPACITY = 0.14;
/** Die Spanne einer Reihe aus lauter Nullen: glatte Marken statt sechsmal „0 €“ (Sichtprobe Aufgabe 12). */
const EMPTY_RANGE_CENTS = 100_000;
/** Rechenbreite eines Zeichens der Zonenbeschriftung bei `AXIS_FONT_PX`, großzügig: Sie entscheidet, ob der Text in seinen Abschnitt passt. */
const ZONE_LABEL_CHAR_PX = 6;
/** Die Wertachse liegt über den Flächen (z 2), damit ihre Beschriftung lesbar bleibt, und unter der Saldotreppe (z 3). */
const VALUE_AXIS_Z = 2.5;
/** Bis zu dieser Spanne des Fensters beschriftet die Detailachse Wochen statt Monate. */
const WEEK_LABELS_MAX_DAYS = 92;
/** Luft der Wertachse über und unter dem äußersten Wert. */
const RANGE_MARGIN = 0.04;

/* ------------------------------------------------------------------ Kalender und Beträge */

const isoDay = (ms: number): string => new Date(ms).toISOString().slice(0, 10);
/** „28.12.2026“. */
const fullDay = (day: string): string => `${shortDay(day)}${day.slice(0, 4)}`;
const yearOf = (day: string): string => day.slice(0, 4);
const quarterOf = (day: string): number => Math.ceil(Number(day.slice(5, 7)) / 3);
const monthsBetween = ({ from, to }: Span): number =>
  (Number(yearOf(to)) - Number(yearOf(from))) * 12 + Number(to.slice(5, 7)) - Number(from.slice(5, 7)) + 1;

const WHOLE_EURO = new Intl.NumberFormat("de-DE", { maximumFractionDigits: 0 });

/** Ganze Euro, kaufmännisch symmetrisch gerundet: „21.140 €“, -50 Cent → „-1 €“, -49 Cent → „0 €“. */
export function euro(cents: number): string {
  const whole = Math.floor((Math.abs(cents) + 50) / 100);
  return `${cents < 0 && whole > 0 ? "-" : ""}${WHOLE_EURO.format(whole)} €`;
}

/* ------------------------------------------------------------------ Abschnitte */

export type ZoneBox = { zone: Zone; left: number; width: number };

const FAR_SHARE_PER_MONTH: Readonly<Record<Exclude<Zone, "DETAIL">, number>> =
  { QUARTERS: QUARTER_SHARE / 3, YEARS: YEAR_SHARE / 12 };

/** Die Abschnitte in Pixeln: Fernzonen nach ihrer Länge, die Detailzone der Rest; am Telefon nur Detail, bei 5 Jahren nur Jahre. */
export function zoneBoxes(s: LiquiditySeries, widthPx: number, phone: boolean): ZoneBox[] {
  if (s.granularity === "FIVE_YEARS") return [{ zone: "YEARS", left: 0, width: widthPx }];
  if (phone) return [{ zone: "DETAIL", left: 0, width: widthPx }];
  const far = s.zones.flatMap((z) => (z.kind === "DETAIL" ? []
    : [{ zone: z.kind, width: Math.round(widthPx * FAR_SHARE_PER_MONTH[z.kind] * monthsBetween(z)) }]));
  const detail = Math.max(0, widthPx - far.reduce((n, b) => n + b.width + ZONE_GAP_PX, 0));
  let left = 0;
  return [{ zone: "DETAIL" as Zone, width: detail }, ...far].map((b) => {
    const box = { zone: b.zone, left, width: b.width };
    left += b.width + ZONE_GAP_PX;
    return box;
  });
}

/** Der Schlüssel der Abschnitte. `useDiagramm` mischt Optionen (`notMerge: false`): Entfallene Achsen und Serien blieben stehen.
 *  Bei einem neuen Schlüssel leert der Verbraucher die Instanz (`instanz.clear()`), bevor die neue Option kommt. Innerhalb
 *  eines Schlüssels hat jede Option dieselben Serien in derselben Folge. */
export function chartLayoutKey(s: LiquiditySeries, phone: boolean): string {
  return zoneBoxes(s, 0, phone).map((b) => b.zone).join("|");
}

const detailZone = (s: LiquiditySeries): Span => s.zones.find((z) => z.kind === "DETAIL") ?? { from: s.today, to: s.horizonEnd };

/** Der Zeitraum eines Abschnitts: Die 5-Jahressicht beginnt am 01.01. des Folgejahrs, sonst gilt die Zone. */
function zoneSpan(s: LiquiditySeries, zone: Zone): Span {
  if (s.granularity === "FIVE_YEARS") return { from: `${Number(yearOf(s.today)) + 1}-01-01`, to: s.horizonEnd };
  return s.zones.find((z) => z.kind === zone) ?? detailZone(s);
}

function zoneLabel(s: LiquiditySeries, zone: Zone): string {
  const { from, to } = zoneSpan(s, zone);
  if (zone === "DETAIL") return T.zone.DETAIL(fullDay(to));
  return zone === "QUARTERS" ? T.zone.QUARTERS(yearOf(from)) : T.zone.YEARS(yearOf(from), yearOf(to));
}

/* ------------------------------------------------------------------ Stapel, Wertachse, Treppen */

/** Divisor der Monatsmittel (A12): Quartale und Jahre zeigen die Fläche je Monat. */
export function monthsIn(p: LiquidityPeriod): number {
  return p.zone === "QUARTERS" ? 3 : p.zone === "YEARS" ? 12 : 1;
}

/** `amountsCents` trägt nur Kategorien ≠ 0 (H17). */
export function categoryCents(p: LiquidityPeriod, key: LiquidityCategory): number {
  return p.amountsCents[key] ?? 0;
}

/** Stapel „samesign“ in fester Folge: Positives wächst über, Negatives unter die Nulllinie (Plan P10, RF3). */
export function stackRects(p: LiquidityPeriod, categories: LiquiditySeries["categories"]):
    { key: LiquidityCategory; y0: number; y1: number }[] {
  let above = 0, below = 0;
  return categories.flatMap(({ key }) => {
    const value = categoryCents(p, key) / monthsIn(p);
    if (value === 0) return [];
    const y0 = value > 0 ? above : below;
    if (value > 0) above += value; else below += value;
    return [{ key, y0, y1: y0 + value }];
  });
}

const minimumOf = (s: LiquiditySeries): number | null => (s.companyIds.length === 1 ? s.scenario.minimumBalanceCents : null);

/** Auf glatte Werte nach außen, mit etwas Luft; eine Reihe aus lauter Nullen bleibt eine Spanne. In Cent. */
export function valueRange(s: LiquiditySeries): { min: number; max: number } {
  const stacks = s.granularity === "MONTH_END" ? [] : s.periods.flatMap((p) => stackRects(p, s.categories).map((r) => r.y1));
  const values = [0, minimumOf(s) ?? 0, s.anchor?.closingCents ?? 0, ...s.dailyClosingCents, ...(s.compareDailyClosingCents ?? []),
    ...stacks, ...s.periods.flatMap((p) => [p.closingCents, p.minCents, p.compareClosingCents ?? 0])];
  const low = Math.min(...values), high = Math.max(...values);
  const margin = (high - low) * RANGE_MARGIN;
  const step = 10 ** Math.floor(Math.log10(high - low || 1)) / 5;
  const min = Math.floor((low - (low < 0 ? margin : 0)) / step) * step;
  const max = Math.ceil((high + (high > 0 ? margin : 0)) / step) * step;
  return { min, max: max > min ? max : min + EMPTY_RANGE_CENTS };
}

/** Die Stützstellen der Saldotreppe für `step: "end"`: Jeder Wert gilt von seinem Punkt bis zum nächsten. Detail je Tag (Punkt am
 *  Tagesbeginn mit dem Stand am Tagesende), sonst je Periode (Punkt am Beginn mit dem Stand am Ende), ein Schlusspunkt danach;
 *  die 5-Jahressicht beginnt mit dem Ausgangsstand. */
export function stairPoints(s: LiquiditySeries, zone: Zone, compare: boolean): [number, number][] {
  const daily = compare ? s.compareDailyClosingCents : s.dailyClosingCents;
  const periods = s.periods.filter((p) => p.zone === zone);
  const byDay = zone === "DETAIL" && daily !== null && daily.length > 0;
  const points: [number, number][] = byDay ? daily.map((c, k) => [dayMs(s.today) + k * DAY_MS, c])
    : periods.flatMap((p) => {
      const value = compare ? p.compareClosingCents : p.closingCents;
      return value === null ? [] : [[dayMs(p.start), value] as [number, number]];
    });
  const [first] = points, last = points.at(-1);
  if (first === undefined || last === undefined) return [];
  const endMs = byDay ? last[0] + DAY_MS : dayMs(periods.at(-1)?.end ?? s.today) + DAY_MS;
  return [...(!compare && s.anchor !== null ? [[first[0], s.anchor.closingCents] as [number, number]] : []), ...points, [endMs, last[1]]];
}

/** Aneinandergrenzende Zeiträume zusammen. */
function joined(spans: readonly Span[]): Span[] {
  const out: Span[] = [];
  for (const span of spans) {
    const last = out.at(-1);
    if (last !== undefined && addDays(last.to, 1) === span.from) last.to = span.to;
    else out.push({ ...span });
  }
  return out;
}

/** Zeiträume unter dem Mindestsaldo (nur bei einem Unternehmen, P9): im Detail die Tage, sonst die ganzen Perioden, je Zone für sich. */
export function belowMinimumRanges(s: LiquiditySeries): Span[] {
  const minimum = minimumOf(s);
  if (minimum === null) return [];
  const days = s.dailyClosingCents.flatMap((c, k) => {
    const day = addDays(s.today, k);
    return c < minimum ? [{ from: day, to: day }] : [];
  });
  const inZone = (zone: Zone): Span[] =>
    s.periods.filter((p) => p.zone === zone && p.belowMinimum).map((p) => ({ from: p.start, to: p.end }));
  return [...joined(s.dailyClosingCents.length > 0 ? days : inZone("DETAIL")), ...joined(inZone("QUARTERS")), ...joined(inZone("YEARS"))];
}

/* ------------------------------------------------------------------ Fenster der Detailzone */

/** `null` = ganze Detailzone. */
export type DetailWindow = Span | null;

const whole = (s: LiquiditySeries, w: Span): DetailWindow => {
  const zone = detailZone(s);
  return w.from === zone.from && w.to === zone.to ? null : w;
};

/** Blättern um die halbe Spanne; an der Zone geklemmt, die Spanne bleibt. */
export function shiftWindow(s: LiquiditySeries, w: DetailWindow, dir: -1 | 1): DetailWindow {
  if (w === null) return null;
  const zone = detailZone(s), span = tageZwischen(w.from, w.to);
  const from = addDays(w.from, dir * Math.max(1, Math.floor((span + 1) / 2)));
  if (from < zone.from) return whole(s, { from: zone.from, to: addDays(zone.from, span) });
  const to = addDays(from, span);
  return to > zone.to ? whole(s, { from: addDays(zone.to, -span), to: zone.to }) : { from, to };
}

/** Das Fenster aus den Grenzen einer Zoom-Geste: auf Tage gerundet, an der Zone geklemmt. */
export function windowFromZoom(s: LiquiditySeries, startMs: number, endMs: number): DetailWindow {
  const zone = detailZone(s), w = windowFromMs(startMs, endMs);
  return whole(s, { from: w.from < zone.from ? zone.from : w.from, to: w.to > zone.to ? zone.to : w.to });
}

/* ------------------------------------------------------------------ Beschriftung */

/** „KW 45 · 02.11.–08.11.“, „Nov 2026“, „Q3 2028“, „2029“, am Tag „Mo 02.11.2026“. */
export function periodLabel(p: LiquidityPeriod, g: Granularity): string {
  if (p.zone === "QUARTERS") return T.period.quarter(quarterOf(p.start), yearOf(p.start));
  if (p.zone === "YEARS") return yearOf(p.start);
  if (g === "DAY") return heuteBeschriftung(p.start);
  if (g === "WEEK") return T.period.week(isoWeek(p.start), shortDay(p.start), shortDay(p.end));
  return monatBeschriftung(`${p.start.slice(0, 8)}01`);
}

/** Die 5-Jahressicht kennt die Tage vor dem 01.01. nicht: Ihr Tiefststand gilt nur für diese Jahre (Ruling G2). */
function shownYears(s: LiquiditySeries): [string, string] {
  const { from, to } = zoneSpan(s, "YEARS");
  return [yearOf(from), yearOf(to)];
}

/** Der tiefste Stand aller Perioden, bei Gleichstand der früheste. */
export function lowestOf(s: LiquiditySeries): { cents: number; date: string } {
  let low = { cents: s.periods[0]?.minCents ?? s.startBalanceCents, date: s.periods[0]?.minDate ?? s.today };
  for (const p of s.periods) if (p.minCents < low.cents) low = { cents: p.minCents, date: p.minDate };
  return low;
}

/** Die Zusammenfassung des Diagramms für `aria-label` (Spec „Barrierefreiheit“). */
export function chartAriaLabel(s: LiquiditySeries): string {
  const low = lowestOf(s), end = fullDay(s.horizonEnd), amount = euro(low.cents), day = fullDay(low.date);
  return s.granularity === "FIVE_YEARS" ? T.chartLabelYears(end, ...shownYears(s), amount, day) : T.chartLabel(end, amount, day);
}

/* ------------------------------------------------------------------ Diagrammoption */

export type ChartInput = { series: LiquiditySeries; theme: DiagrammThema; widthPx: number; phone: boolean; nowMs: number;
  window: DetailWindow; frameMode: boolean };

type RenderApi = { value: (dim: number) => number; coord: (point: [number, number]) => number[] };
type Option = Record<string, unknown>;

/** Zehn Kategorien auf den acht Farbplätzen: Die neunte und zehnte liegen unter der Nulllinie, ihre Farbgeber darüber. */
export function categoryColor(theme: DiagrammThema, index: number): string {
  return theme.color[index % theme.color.length] ?? theme.text;
}

/** Die Einträge der Legende in Farbe und Form des Diagramms (Fläche, Linie, gestrichelt), auch ungenutzte. */
export function legendEntries(theme: DiagrammThema): { key: string; label: string; color: string; form: "AREA" | "LINE" | "DASHED" }[] {
  const categories = (Object.keys(T.category) as LiquidityCategory[])
    .map((key, k) => ({ key, label: T.category[key], color: categoryColor(theme, k), form: "AREA" as const }));
  return [...categories, { key: "balance", label: T.legend.balance, color: theme.text, form: "LINE" },
    { key: "compare", label: T.legend.compare, color: theme.leise, form: "DASHED" },
    { key: "minimum", label: T.legend.minimum, color: theme.fehler, form: "DASHED" }];
}

const rectOf = (fill: string) => (_: unknown, api: RenderApi): Option => {
  const [x0 = 0, y0 = 0] = api.coord([api.value(0), api.value(2)]);
  const [x1 = 0, y1 = 0] = api.coord([api.value(1), api.value(3)]);
  return { type: "rect", shape: { x: x0, y: Math.min(y0, y1), width: x1 - x0, height: Math.abs(y1 - y0) }, style: { fill } };
};

/** Die unsichtbare Trefferfläche über einer Stufe: Eine `line`-Serie trifft nur an Symbolen, das Popup soll die ganze Stufe tragen. */
const stairHit = (_: unknown, api: RenderApi): Option => {
  const [x0 = 0, y = 0] = api.coord([api.value(0), api.value(2)]);
  const [x1 = 0] = api.coord([api.value(1), api.value(2)]);
  return { type: "rect", shape: { x: x0, y: y - HIT_PX, width: x1 - x0, height: 2 * HIT_PX }, style: { fill: "transparent" } };
};

const lowestMark = (fill: string, ring: string) => (_: unknown, api: RenderApi): Option => {
  const [x = 0, y = 0] = api.coord([api.value(0), api.value(1)]);
  return { type: "polygon", shape: { points: [[x, y - MARK_PX], [x + MARK_PX, y], [x, y + MARK_PX], [x - MARK_PX, y]] },
    style: { fill, stroke: ring, lineWidth: 1 } };
};

/** Die Beschriftungen der Zeitachse: Perioden in Fernzonen, im Detail Monate, in einem kurzen Fenster Wochen. Eine Marke, deren
 *  linksbündiger Text am rechten Rand in den nächsten Abschnitt liefe, entfällt. */
function axisStops(s: LiquiditySeries, box: ZoneBox, w: DetailWindow): Map<number, string> {
  if (box.zone !== "DETAIL") {
    return new Map(s.periods.filter((p) => p.zone === box.zone).map((p) =>
      [dayMs(p.start), box.zone === "QUARTERS" ? T.period.quarterShort(quarterOf(p.start)) : yearOf(p.start)]));
  }
  const span = zoneSpan(s, box.zone), visible = w ?? span;
  const weeks = tageZwischen(visible.from, visible.to) < WEEK_LABELS_MAX_DAYS;
  const edgeMs = dayMs(visible.to) + DAY_MS, reserveMs = AXIS_LABEL_PX * (edgeMs - dayMs(visible.from)) / box.width;
  const stops = new Map<number, string>();
  for (let day = span.from; day <= span.to; day = addDays(day, 1)) {
    const ms = dayMs(day), atEdge = ms <= edgeMs && edgeMs - ms < reserveMs;
    if (!atEdge && (weeks ? weekday(day) === 0 : day.endsWith("-01"))) stops.set(ms, weeks ? shortDay(day) : monatBeschriftung(day));
  }
  return stops;
}

/** Eine Zeile des Popups; `color` ist die Farbmarke der Kategorie (Hausregel: „Die Farbmarken der Serien bleiben farbig“). */
export type PopupLine = { text: string; color: string | null };
const plain = (text: string): PopupLine => ({ text, color: null });

const compareLines = (closing: number, compare: number | null): PopupLine[] =>
  (compare === null ? [] : [plain(T.popup.compare(euro(compare))), plain(T.popup.delta(euro(closing - compare)))]);

const stairPopup = (label: string, closing: number, compare: number | null): PopupLine[] =>
  [plain(label), plain(T.popup.balance(euro(closing))), ...compareLines(closing, compare)];

function periodPopup(s: LiquiditySeries, t: DiagrammThema, p: LiquidityPeriod): PopupLine[] {
  const amounts = s.categories.flatMap(({ key }, k) => {
    const cents = categoryCents(p, key);
    return cents === 0 ? [] : [{ text: T.popup.category(T.category[key], euro(cents)), color: categoryColor(t, k) }];
  });
  return [plain(periodLabel(p, s.granularity)), ...amounts, plain(T.popup.closing(euro(p.closingCents))),
    plain(T.popup.lowest(euro(p.minCents), fullDay(p.minDate))), ...compareLines(p.closingCents, p.compareClosingCents)];
}

/** Das Popup je getroffener Serie: Flächen und Tiefststand die Periode, die Saldotreppe Tag und Stand (in Fernzonen die Periode). */
export function popupLines(s: LiquiditySeries, t: DiagrammThema): (p: { seriesName?: string; name?: string }) => PopupLine[] {
  const byStart = new Map(s.periods.map((p) => [p.start, p]));
  return ({ seriesName, name = "" }) => {
    const p = byStart.get(name);
    if (seriesName === "balanceHit") {
      const k = tageZwischen(s.today, name), daily = s.dailyClosingCents[k];
      if (daily !== undefined) return stairPopup(heuteBeschriftung(name), daily, s.compareDailyClosingCents?.[k] ?? null);
      return p === undefined ? [] : stairPopup(periodLabel(p, s.granularity), p.closingCents, p.compareClosingCents);
    }
    return p === undefined ? [] : periodPopup(s, t, p);
  };
}

/** Der Knoten des Popups, erst beim Zeigen gebaut, Text nur über `textContent` (P4, Muster TicketTimelineChart). */
function popupNode(lines: readonly PopupLine[]): HTMLElement | string {
  if (lines.length === 0) return "";
  const node = document.createElement("div");
  for (const { text, color } of lines) {
    const row = node.appendChild(document.createElement("div"));
    if (color !== null) {
      const mark = row.appendChild(document.createElement("span"));
      mark.style.cssText = "display:inline-block;width:8px;height:8px;margin-right:4px";
      mark.style.background = color;
    }
    row.append(text);
  }
  return node;
}

/** richText kennt nur die Marke der getroffenen Serie, zieht einen Rand in deren Farbe und setzt die Zeilenhöhe fest auf 22 px. */
function popup(s: LiquiditySeries, t: DiagrammThema): Record<string, unknown> {
  const regel = hausregelTooltip(), lines = popupLines(s, t);
  return { ...regel, renderMode: "html", appendTo: "body", className: "liq__popup", extraCssText: `${String(regel.extraCssText ?? "")}z-index:var(--z-popup);`,
    formatter: (p: { seriesName?: string; name?: string }) => popupNode(lines(p)) };
}

/** Linien des Abschnitts: Nulllinie, Mindestsaldo (innen beschriftet nur im ersten), „jetzt“ im ersten, wenn die Achse ihn zeigt. */
function zoneLines(i: ChartInput, n: number, span: Span): Option[] {
  const t = i.theme, minimum = minimumOf(i.series);
  const showsNow = n === 0 && i.nowMs >= dayMs(span.from) && i.nowMs < dayMs(span.to) + DAY_MS;
  return [
    { yAxis: 0, lineStyle: { type: "solid", color: t.linieStark, width: 1 }, label: { show: false } },
    ...(minimum === null ? [] : [{ yAxis: minimum, lineStyle: { type: "dashed", color: t.fehler, width: 1 }, label: n > 0 ? { show: false }
      : { show: true, formatter: T.minimumLine(euro(minimum)), position: "insideStartTop", color: t.fehler, fontSize: AXIS_FONT_PX } }]),
    // Innen und waagerecht lesbar: Die Positionen „inside…“ drehen den Text mit der Linie; hier das Linienende, nach innen versetzt.
    ...(showsNow ? [{ xAxis: i.nowMs, lineStyle: { type: "solid", color: t.jetzt, width: 1 }, label: { show: true, formatter: T.now,
      position: "end", distance: [0, -4], align: "left", verticalAlign: "top", padding: [0, 0, 0, 4], color: t.jetzt, fontSize: AXIS_FONT_PX } }] : []),
  ];
}

/** Flächen des Abschnitts: Zonenbeschriftung (Fernzonen getönt, geringere Auflösung, mehr Fortschreibung), Tage unter dem Mindestsaldo. */
function zoneAreas(i: ChartInput, box: ZoneBox, span: Span): Option[][] {
  const s = i.series, t = i.theme;
  const ranges = belowMinimumRanges(s);
  const tinted = s.granularity !== "FIVE_YEARS" && box.zone !== "DETAIL";
  const label = (formatter: string, color: string, position: string): Option =>
    ({ show: true, formatter, position, color, fontSize: AXIS_FONT_PX });
  // Schmaler als der Text: keine Beschriftung, sonst liefen die Fernzonen ineinander (Review 6, H11, 768 px mit offener Spalte). Die Achse nennt Quartal und Jahr weiter.
  const zoneText = zoneLabel(s, box.zone);
  return [
    [{ xAxis: dayMs(span.from), itemStyle: { color: tinted ? t.hover : "transparent" },
      label: zoneText.length * ZONE_LABEL_CHAR_PX <= box.width ? label(zoneText, t.leise, "insideTop") : { show: false } }, { xAxis: dayMs(span.to) + DAY_MS }],
    ...ranges.filter((r) => r.from >= span.from && r.to <= span.to).map((r) =>
      [{ xAxis: dayMs(r.from), itemStyle: { color: t.fehler, opacity: BELOW_OPACITY },
        label: r === ranges[0] ? label(T.table.belowMinimum, t.fehler, "insideBottomLeft") : { show: false } },
      { xAxis: dayMs(r.to) + DAY_MS }]),
  ];
}

/** Alle Serien eines Abschnitts, immer in derselben Folge und Zahl (siehe `chartLayoutKey`). */
function zoneSeries(i: ChartInput, box: ZoneBox, n: number): Option[] {
  const s = i.series, t = i.theme;
  const span = zoneSpan(s, box.zone), axes = { xAxisIndex: n, yAxisIndex: n };
  const periods = s.periods.filter((p) => p.zone === box.zone);
  const stacks = s.granularity === "MONTH_END" ? [] : periods.map((p) => ({ p, rects: stackRects(p, s.categories) }));
  const stair = stairPoints(s, box.zone, false);
  const line = (name: string, data: [number, number][], lineStyle: Option, more: Option = {}): Option =>
    ({ ...axes, type: "line", name, step: "end", data, showSymbol: false, silent: true, clip: true, z: 3, lineStyle,
      emphasis: { disabled: true }, ...more });
  return [
    ...s.categories.map(({ key }, k) => ({ ...axes, type: "custom", name: key, clip: true, encode: { x: [0, 1], y: [2, 3] },
      renderItem: rectOf(categoryColor(t, k)),
      data: stacks.flatMap(({ p, rects }) => rects.filter((r) => r.key === key).map((r) =>
        ({ name: p.start, value: [dayMs(p.start), dayMs(p.end) + DAY_MS, r.y0, r.y1] }))) })),
    line("balance", stair, { color: t.text, width: 2 }, {
      markLine: { silent: true, symbol: ["none", "none"], emphasis: { disabled: true }, data: zoneLines(i, n, span) },
      markArea: { silent: true, emphasis: { disabled: true }, data: zoneAreas(i, box, span) } }),
    line("compare", stairPoints(s, box.zone, true), { color: t.leise, width: 1.5, type: "dashed" }),
    { ...axes, type: "custom", name: "balanceHit", clip: true, z: 4, encode: { x: [0, 1], y: 2 }, renderItem: stairHit,
      data: stair.slice(0, -1).flatMap(([x, y], k) => {
        const next = stair[k + 1]![0];
        return next === x ? [] : [{ name: isoDay(x), value: [x, next, y] }];
      }) },
    { ...axes, type: "custom", name: "lowest", clip: true, z: 5, encode: { x: 0, y: 1 }, renderItem: lowestMark(t.text, t.flaeche),
      // Spec „Verdichtung“ (Ruling M1): Marken nur, wo keine Tageslinie läuft; Quartale und Jahre behalten sie bei jeder Verdichtung.
      data: box.zone === "DETAIL" && s.granularity !== "MONTH_END" ? []
        : periods.map((p) => ({ name: p.start, value: [dayMs(p.minDate) + DAY_MS / 2, p.minCents] })) },
  ];
}

/** Die Option für `useDiagramm`: ein `grid`, eine Zeitachse und eine Wertachse je Abschnitt in EINER Instanz (598), gemeinsame Skala. */
export function chartOption(i: ChartInput): Record<string, unknown> {
  const { series: s, theme: t, window: w } = i;
  const boxes = zoneBoxes(s, i.widthPx, i.phone);
  const range = valueRange(s);
  const zone = detailZone(s);
  const zoomed = s.granularity !== "FIVE_YEARS";
  return {
    useUTC: true,
    grid: boxes.map((b) => ({ left: b.left, width: b.width, top: GRID_TOP_PX, bottom: GRID_BOTTOM_PX, containLabel: false,
      outerBoundsMode: "none" })),
    xAxis: boxes.map((b, n) => {
      const span = zoneSpan(s, b.zone), stops = axisStops(s, b, w), customValues = [...stops.keys()];
      return { type: "time", gridIndex: n, min: dayMs(span.from), max: dayMs(span.to) + DAY_MS, splitLine: { show: false },
        axisLine: { onZero: false, lineStyle: { color: t.raster } }, axisTick: { customValues, lineStyle: { color: t.raster } },
        axisLabel: { customValues, formatter: (ms: number) => stops.get(ms) ?? "", color: t.leise, fontSize: AXIS_FONT_PX, align: "left",
          hideOverlap: true } };
    }),
    yAxis: boxes.map((_, n) => ({ type: "value", gridIndex: n, z: VALUE_AXIS_Z, min: range.min, max: range.max, axisTick: { show: false },
      axisLine: { show: n > 0, lineStyle: { color: t.linieStark } }, splitLine: { lineStyle: { color: t.raster } },
      axisLabel: { show: n === 0, inside: true, formatter: euro, color: t.leise, fontSize: AXIS_FONT_PX, verticalAlign: "bottom",
        hideOverlap: true, textBorderColor: t.flaeche, textBorderWidth: 2 } })),
    // Zoom nur für die Detailzone; die 5-Jahressicht hat keine (ohne die leere Liste legte useDiagramm die Vorgabe darunter).
    dataZoom: zoomed ? [{ type: "inside", xAxisIndex: 0, filterMode: "none", zoomOnMouseWheel: false, moveOnMouseWheel: false,
      moveOnMouseMove: !i.frameMode, startValue: dayMs(w?.from ?? zone.from), endValue: dayMs(w?.to ?? zone.to) + DAY_MS }] : [],
    // Versteckt und ohne Symbole: nur Träger von „Zeitraum aufziehen“ (takeGlobalCursor im Verbraucher).
    ...(zoomed ? { toolbox: { show: true, itemSize: 0, showTitle: false, left: 0, top: 0, feature: { dataZoom: { xAxisIndex: 0,
      yAxisIndex: "none", brushStyle: { color: t.raster, opacity: 0.4, borderColor: t.text, borderWidth: 1 } } } } } : {}),
    tooltip: popup(s, t),
    series: boxes.flatMap((b, n) => zoneSeries(i, b, n)),
  };
}

/* ------------------------------------------------------------------ Tabelle */

export type TableRow = { kind: "ZONE"; zone: Zone; label: string }
  | { kind: "ANCHOR"; label: string; closingEuro: string }
  | { kind: "PERIOD"; key: string; label: string; zone: Zone; inEuro: string; outEuro: string; netEuro: string; closingEuro: string;
      minEuro: string; minDate: string; changeEuro: string | null; compareClosingEuro: string | null; deltaEuro: string | null;
      belowMinimum: boolean };

/** Die Zeilen der Tabelle: je Zone eine Zwischenzeile, das Fenster begrenzt nur die Detailzone; bei 5 Jahren Ausgangsstand und Jahre. */
export function tableRows(s: LiquiditySeries, w: DetailWindow): TableRow[] {
  const row = (p: LiquidityPeriod, k: number): TableRow => ({ kind: "PERIOD", key: p.start, label: periodLabel(p, s.granularity), zone: p.zone,
    inEuro: euro(p.inCents), outEuro: euro(p.outCents), netEuro: euro(p.inCents + p.outCents), closingEuro: euro(p.closingCents),
    minEuro: euro(p.minCents), minDate: fullDay(p.minDate),
    changeEuro: s.granularity === "MONTH_END" ? euro(p.closingCents - (s.periods[k - 1]?.closingCents ?? s.startBalanceCents)) : null,
    compareClosingEuro: p.compareClosingCents === null ? null : euro(p.compareClosingCents),
    deltaEuro: p.compareClosingCents === null ? null : euro(p.closingCents - p.compareClosingCents), belowMinimum: p.belowMinimum });
  const rows = s.periods.map((p, k) => ({ p, row: row(p, k) }));
  if (s.granularity === "FIVE_YEARS") {
    const anchor: TableRow[] = s.anchor === null ? []
      : [{ kind: "ANCHOR", label: T.table.anchor(fullDay(s.anchor.date)), closingEuro: euro(s.anchor.closingCents) }];
    return [...anchor, ...rows.map((r) => r.row)];
  }
  const visible = (p: LiquidityPeriod): boolean => p.zone !== "DETAIL" || w === null || (p.end >= w.from && p.start <= w.to);
  return s.zones.flatMap((z): TableRow[] => [{ kind: "ZONE", zone: z.kind, label: zoneLabel(s, z.kind) },
    ...rows.filter(({ p }) => p.zone === z.kind && visible(p)).map((r) => r.row)]);
}

/* ------------------------------------------------------------------ Positionen */

export type PositionGroup = { key: "ACCOUNT" | "IN" | "OUT"; positions: LiquidityPosition[]; sumCents: number };

const POSITION_GROUPS: readonly PositionGroup["key"][] = ["ACCOUNT", "IN", "OUT"];

/** Schlüssel in der Ansicht (P8): Derselbe Lieferant in zwei Unternehmen sind zwei Positionen. */
export const positionKey = (p: LiquidityPosition): string => `${p.companyId}|${p.ref}`;

const hasHint = (p: LiquidityPosition): boolean => p.sourceGone || p.notes.includes("OVERDUE") || p.notes.includes("STALE_BALANCE");
const volume = (p: LiquidityPosition): number => Math.abs(p.inCents) + Math.abs(p.outCents);

/** Drei Gruppen in fester Folge; Positionen mit Hinweis zuerst, dann nach Betrag (Schlechtestes oben); die Summe zählt nur Eingeschaltetes. */
export function positionGroups(ps: readonly LiquidityPosition[], search: string): PositionGroup[] {
  const needle = search.trim().toLocaleLowerCase("de-DE");
  const found = ps.filter((p) => `${p.label} ${p.detail ?? ""}`.toLocaleLowerCase("de-DE").includes(needle));
  return POSITION_GROUPS.map((key) => {
    const positions = found.filter((p) => p.group === key)
      .sort((a, b) => Number(hasHint(b)) - Number(hasHint(a)) || volume(b) - volume(a));
    return { key, positions, sumCents: positions.reduce((n, p) => (p.enabled ? n + p.inCents + p.outCents : n), 0) };
  });
}

/* ------------------------------------------------------------------ Entwurf (P12, 597) */

export type DraftChange = { enabled?: boolean; freeze?: boolean };
/** Schlüssel: ref. */
export type Draft = ReadonlyMap<string, DraftChange>;

/** Dreht ein Feld: Steht es im Entwurf, fällt es heraus, sonst kommt es mit `value` hinein. Die Vorschau zeigt die Position
 *  schon umgestellt, deshalb zählt zuerst der Entwurf und nicht der Stand der Position. */
function toggled(d: Draft, ref: string, field: keyof DraftChange, value: boolean): Draft {
  const { [field]: had, ...others } = d.get(ref) ?? {};
  const change: DraftChange = had === undefined ? { ...others, [field]: value } : others;
  const next = new Map(d);
  if (Object.keys(change).length === 0) next.delete(ref); else next.set(ref, change);
  return next;
}

export function toggleEnabled(d: Draft, p: LiquidityPosition): Draft {
  return toggled(d, p.ref, "enabled", !p.enabled);
}

/** „Einfrieren“ oder das Einfrieren lösen; ein Konto friert nicht. */
export function toggleFreeze(d: Draft, p: LiquidityPosition): Draft {
  return p.kind === "ACCOUNT" ? d : toggled(d, p.ref, "freeze", p.frozen === null);
}

/** Die Vorschau der Reihe: „jetzt einfrieren“ ändert sie nicht und erzeugt keinen `override` (P12). */
export function draftOverrides(d: Draft): string[] {
  return [...d].flatMap(([ref, c]) => [...(c.enabled === undefined ? [] : [`${ref}:${c.enabled ? "on" : "off"}`]),
    ...(c.freeze === false ? [`${ref}:live`] : [])]);
}

export function draftChanges(d: Draft): { ref: string; enabled?: boolean; freeze?: boolean }[] {
  return [...d].map(([ref, c]) => ({ ref, ...c }));
}

export function draftCounts(d: Draft): DraftCounts {
  const changes = [...d.values()];
  const n = (f: (c: DraftChange) => boolean): number => changes.filter(f).length;
  return { off: n((c) => c.enabled === false), on: n((c) => c.enabled === true), frozen: n((c) => c.freeze === true),
    live: n((c) => c.freeze === false) };
}

/* ------------------------------------------------------------------ Hinweise */

export type Hint = { kind: "BELOW_MINIMUM" | "LOWEST" | "OVERDUE_IN" | "OVERDUE_OUT" | "STALE" | "NO_ACCOUNT" | "WITHOUT_PLAN"
  | "BEYOND" | "MISSING"; text: string };
type Named = { id: string; name: string };

const withName = (list: readonly Named[], id: string, text: string): string => {
  const name = list.find((x) => x.id === id)?.name;
  return name === undefined ? text : T.warning.named(name, text);
};

/** Die Hinweiszeile, Wichtigstes zuerst (Spec „Tabelle“). `companies` nennt bei mehreren Unternehmen, wen es trifft (593). */
export function hints(s: LiquiditySeries, accounts: readonly Named[], companies: readonly Named[] = []): Hint[] {
  const w = s.warnings, low = lowestOf(s);
  const below = [...w.belowMinimum].sort((a, b) => a.minCents - b.minCents).map((b): Hint => ({ kind: "BELOW_MINIMUM",
    text: withName(companies, b.companyId, T.warning.belowMinimum(euro(b.minCents), fullDay(b.minDate), fullDay(b.firstDate))) }));
  const when = (on: boolean, kind: Hint["kind"], text: () => string): Hint[] => (on ? [{ kind, text: text() }] : []);
  return [
    ...below,
    ...when(below.length === 0, "LOWEST", () => (s.granularity === "FIVE_YEARS"
      ? T.warning.lowestInYears(...shownYears(s), euro(low.cents), fullDay(low.date)) : T.warning.lowest(euro(low.cents), fullDay(low.date)))),
    ...when(w.overdue.in.count > 0, "OVERDUE_IN", () => T.warning.overdueIn(w.overdue.in.count, euro(w.overdue.in.cents))),
    ...when(w.overdue.out.count > 0, "OVERDUE_OUT", () => T.warning.overdueOut(w.overdue.out.count, euro(w.overdue.out.cents))),
    ...w.staleAccounts.map((a): Hint => ({ kind: "STALE",
      text: withName(accounts, a.accountId, T.warning.staleBalance(fullDay(a.balanceDate), tageZwischen(a.balanceDate, s.today))) })),
    ...when(w.accountsMissing, "NO_ACCOUNT", () => T.warning.noAccount),
    ...when(w.ordersWithoutPlan.length > 0, "WITHOUT_PLAN", () => T.warning.withoutPlan(w.ordersWithoutPlan.length)),
    ...when(w.beyondHorizon.count > 0, "BEYOND", () => T.warning.beyond(w.beyondHorizon.count, euro(w.beyondHorizon.cents))),
    ...when(s.missingSources.length > 0, "MISSING", () => T.warning.missing(s.missingSources.map((m) => T.source[m]).join(", "))),
  ];
}
